import { motion } from "framer-motion";
import { Trophy, GraduationCap, Compass } from "lucide-react";
import { TRAVEL } from "@/constants/testIds";

const features = [
  {
    icon: Trophy,
    title: "High-Level Competition",
    desc: "Our travel teams play the toughest schedules — built to challenge and elevate every player on the roster.",
  },
  {
    icon: GraduationCap,
    title: "Collegiate Connections",
    desc: "Our program director is highly connected in college ranks and guides each family through every step of the process.",
  },
  {
    icon: Compass,
    title: "The Right Way",
    desc: "Discipline, IQ, and team-first basketball. We play the game with intention — not just talent.",
  },
];

export default function TravelTeams() {
  return (
    <section
      id="travel"
      data-testid={TRAVEL.section}
      className="relative overflow-hidden"
    >
      <div className="relative h-[460px] sm:h-[560px]">
        <img
          src="/gallery/team-arena.jpg"
          alt="Team BBA squad on the arena floor"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/85 to-[#050B14]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] to-transparent" />

        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex items-center">
          <div className="max-w-2xl">
            <div className="overline">Travel Teams</div>
            <div className="gold-divider mt-4 mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Play high-level basketball,{" "}
              <span className="text-[#B8C5D6]">the right way.</span>
            </h2>
            <p className="mt-6 text-[#A0ABC0] text-base sm:text-lg max-w-xl">
              We compete at the highest tier of travel basketball with a
              philosophy rooted in IQ, discipline, and player development.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#050B14] -mt-20 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="bba-card p-7"
                >
                  <div className="w-12 h-12 grid place-items-center border border-[#B8C5D6]/40 text-[#B8C5D6] mb-6">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl mb-3 tracking-tight">
                    {f.title}
                  </h3>
                  <p className="text-[#A0ABC0] text-sm leading-relaxed">
                    {f.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Team gallery — real photos */}
          <div className="mt-20">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <div className="overline">Team BBA · In Action</div>
                <div className="gold-divider mt-4 mb-4" />
                <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
                  Live basketball. Real players.{" "}
                  <span className="text-[#B8C5D6]">Everywhere we go.</span>
                </h3>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              <motion.figure
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="bba-card overflow-hidden p-0 group"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0A1128]">
                  <img
                    src="/gallery/team-jr-eybl.jpg"
                    alt="Team BBA at the Jr. EYBL"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="p-5 flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg tracking-tight">
                      Jr. EYBL Circuit
                    </div>
                    <div className="text-xs text-[#A0ABC0] mt-0.5">
                      Elite Nike youth competition
                    </div>
                  </div>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#93A5C0] border border-white/15 px-2 py-1">
                    Team BBA
                  </span>
                </figcaption>
              </motion.figure>

              <motion.figure
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bba-card overflow-hidden p-0 group"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0A1128]">
                  <img
                    src="/gallery/team-arena.jpg"
                    alt="Team BBA on the arena floor"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="p-5 flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg tracking-tight">
                      National Tournament Play
                    </div>
                    <div className="text-xs text-[#A0ABC0] mt-0.5">
                      Multi-level arena competition
                    </div>
                  </div>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#93A5C0] border border-white/15 px-2 py-1">
                    Team BBA
                  </span>
                </figcaption>
              </motion.figure>
            </div>
          </div>

          <div className="mt-14 flex justify-center">
            <a
              href="#contact"
              data-testid={TRAVEL.ctaInquiry}
              className="btn-gold inline-flex items-center gap-2 px-7 py-4 text-sm uppercase"
            >
              Inquire About Tryouts
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
